import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { LEGAL_FAQ_CATEGORIES } from "@/content/legal-corpus/faq-defs";

const ADVICE_EN = [
  "Check the counterparty's company affidavit and signing authority at the Department of Business Development before signing — a signatory without authority may not bind the company.",
  "Use a bilingual contract with an explicit controlling-language clause, and set the governing law and dispute-resolution mechanism in the same document.",
  "Assess prescription before negotiating: tort claims run one year from knowledge of the act and the wrongdoer, and compoundable offences require a complaint within three months.",
  "Preserve evidence from day one in a form a court accepts: original contracts, receipts, transfer slips, emails and chat records showing dates and participants.",
  "Register any lease of immovable property longer than three years at the Land Office; otherwise section 538 limits enforceability to three years even if the contract says 30.",
  "If you choose arbitration, specify the institution, number of arbitrators, seat and language, so no time is lost arguing procedure.",
  "Translate key documents into Thai early if Thai court proceedings are likely — the court works in Thai and requires Thai translations to be filed.",
  "Before designing a shareholding structure, check whether the activity falls under the Foreign Business Act lists, then choose a transparent route: licence, BOI promotion, or the Treaty of Amity.",
];

const CAUTIONS_EN = [
  "Using a foreign template contract unchanged, leaving clauses that Thai courts will not enforce under the Civil and Commercial Code.",
  "Setting a very large penalty with no basis for the figure, which the court may reduce under section 383 — so it adds less protection than assumed.",
  "Terminating verbally or omitting the ground from the termination letter, which bars reliance on the section 119 severance exceptions later.",
  "Using Thai nominee shareholders to bypass foreign-ownership limits, which section 36 penalises on both sides and can lead to a court order to cease the business.",
  "Signing a shareholder agreement without amending the articles of association, so the terms do not bind the company or third parties.",
  "Negotiating privately in a fraud case past the three-month window, which time-bars the criminal complaint.",
  "Posting criticism of the opposing party online during a dispute, inviting a separate defamation case under section 328.",
  "Letting more than ten years pass after a final judgment without starting execution, which extinguishes the right to enforce it.",
];

export const Route = createFileRoute("/en/legal/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/legal-corpus");
    return { faqs: m.LEGAL_FAQ_EN };
  },
  component: EnLegalFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Thai Legal Advisory for Foreigners — ${faqs.length} FAQs`,
        description:
          "Bilingual contract drafting and review, Thai labour law and section 118 severance, leases and property disputes, shareholder agreements and nominee risk, prescription and enforcement, arbitration, and certifying legal documents.",
        path: "/en/legal/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/legal/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Thai Legal Advisory FAQ", url: `${site}/en/legal/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/legal/faq")) },
      ],
    };
  },
});

function EnLegalFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Legal Advisory</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Thai Legal Advisory for Foreigners: Contracts, Employment and Disputes
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          {faqs.length} answers grounded in the Civil and Commercial Code, the Civil and Criminal Procedure Codes,
          the Labour Protection Act B.E. 2541, the Arbitration Act B.E. 2545 and the Foreign Business Act B.E. 2542.
        </p>

        <section aria-label="Topics" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Browse by topic</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {LEGAL_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/en/legal/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleEn}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descEn}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_EN} locale="en" />
        <ServiceCautionBlock items={CAUTIONS_EN} locale="en" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Have a Thai lawyer assess your matter first</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the nature of the dispute, the parties, the key dates and the documents you hold — by phone, LINE or email, Monday to Saturday 9:00–18:00.
          </p>
          <a href="/en/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact our team</a>
        </section>
        <FaqSourceNote topic="legal" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}